import {memo, useCallback, useMemo} from 'react';

import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {getLocalizationService} from '~/shared/services/localisationService';
import TenbisCredit from '~/assets/images/icons/tenbisCredit.svg?react';
import {media} from '~/shared/theme/media';
import actions from '~/shared/store/actions';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {trackEvent} from '~/shared/services/analytics';
import {TenBisCreditEventTypes} from '~/shared/services/analytics/analyticsModels';
import PieButton from '~/shared/theme/pie/PieButton';

const Root = styled.div`
  padding: 16px;
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 12px;
  background: ${({theme}) => theme.colors.disclaimerSurface};
  ${media.minMobile`
    flex-direction: row;
    align-items: center;
  `}
`;

const Title = styled.div`
  color: ${({theme}) => theme.colors.contentDefault};
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
`;

const EnabledTitle = styled(Title)`
  text-align: center;
  padding-top: 16px;
  min-width: 160px;
  ${flipOnLTR`
    text-align: center;
  `}
  ${media.minMobile`
    text-align: inherit;
    padding-top: 0;
    text-align: left;
    ${flipOnLTR`
      margin-right: 16px;
    `}
  `}
`;

const SubTitle = styled.div`
  color: ${({theme}) => theme.colors.contentInteractiveSubdued};
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
`;

const AddCreditButton = styled(PieButton)`
  min-width: 206px;
  max-height: 44px;
  margin-top: 16px;
  ${media.minMobile`
    margin-top: 0;
  `}
`;

const IconWrapper = styled.div<{disabled?: boolean}>`
  display: flex;
  align-items: center;
  ${flipOnLTR`
    margin-left: 6px;  
  `}

  path {
    fill: ${({theme, disabled}) => (disabled ? theme.colors.contentDisabled : theme.colors.surface)};
  }
`;

const Link = styled.a`
  color: ${({theme}) => theme.colors.contentLink};
  font-weight: bold;
  ${flipOnLTR`
    margin-right: 8px;
  `}
`;

type IAddToCredit = {
  availableAmount: number;
  moneycardId: string;
  encryptedMoneycardId: string;
  showAutoCredit: boolean;
  isAutoCreditActive: boolean;
  setOrderListData: () => Promise<void>;
  onAutoCreditSubscribeSuccess: () => void;
};

const INTERCOM_LINK = 'https://intercom-help.eu/10bis/he/articles/81353';

const AddToCredit = ({availableAmount, moneycardId, encryptedMoneycardId, setOrderListData, showAutoCredit, isAutoCreditActive, onAutoCreditSubscribeSuccess}: IAddToCredit) => {
  const {t} = getLocalizationService();
  const dispatch = useDispatch();

  const openTransferCreditModal = useCallback(() => {
    dispatch(
      actions.setCurrentModal('tenbisCreditChargeModal', {
        hideCloseButton: true,
        showAutoCredit,
        availableBalance: availableAmount,
        moneycardId,
        encryptedMoneycardId,
        setOrderListData,
        isAutoCreditActive,
        onAutoCreditSubscribeSuccess,
      }),
    );

    trackEvent('handleTenBisCredit', {type: 'click'});

  }, [
    dispatch,
    showAutoCredit,
    availableAmount,
    moneycardId,
    encryptedMoneycardId,
    setOrderListData,
    isAutoCreditActive,
    onAutoCreditSubscribeSuccess,
  ]);

  const disabled = useMemo(() => {
    return availableAmount <= 0;
  }, [availableAmount]);

  const handleReadMoreAnalytics = () => {
    trackEvent('handleTenBisCredit', {type: TenBisCreditEventTypes.READ_MORE});
  };

  return (
    <Root>
      <div>
        <Title>{disabled ? t('used_all_your_balance') : t('balance_left_on_card')}</Title>
        <SubTitle>
          {disabled ? t('add_to_balance_when_renewed') : t('save_to_cardit_description')}
          <Link target="_blank" rel="noopener" href={INTERCOM_LINK} onClick={handleReadMoreAnalytics}>{t('more_info')}</Link>
        </SubTitle>
      </div>

      {isAutoCreditActive ? (<EnabledTitle>{t('added_automatically')}</EnabledTitle>) : (
        <AddCreditButton disabled={disabled} onClick={openTransferCreditModal} variant="primaryLarge">
          <IconWrapper disabled={disabled}>
            <TenbisCredit />
          </IconWrapper>
          {t('load_to_credit')}
        </AddCreditButton>
      )}
    </Root>
  );
};

export default memo(AddToCredit);
